import { useEffect, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Input,
  Spinner,
  Table as ChakraTable,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react'
import { useAdminArticlePolls } from '@olagg/api-hooks'
import { ArticlePollOptionRow, OlaggModule, PermissionEnum } from '@olagg/db-types'
import { useNavigate, useParams } from 'react-router-dom'
import { ROUTES } from '../../consts/routes'
import useRoles from '../../features/Roles/hooks/useRoles'
import { archetypeLabels, CONTENT_LANGS, langLabels, optionText, sourceLabels } from './constants'

/**
 * Revisión y edición puntual de una encuesta (R23).
 *
 * No es un formulario de alta: la encuesta ya existe y las opciones salen del
 * cuerpo del artículo. Acá se edita la pregunta, se corrigen las etiquetas de las
 * opciones fijas y se retira una opción a mano. Todo eso congela la encuesta, y
 * la pantalla lo dice antes de guardar, porque implica que las opciones dejan de
 * seguir a los códigos del artículo.
 */
const EditArticlePoll = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { missingPermission } = useRoles()
  const { articlePoll: poll, find, updateArticlePoll } = useAdminArticlePolls({ page: 1, limit: 10 })

  const [question, setQuestion] = useState<Record<string, string>>({})
  const [labels, setLabels] = useState<Record<number, Record<string, string>>>({})

  const readOnly = missingPermission(OlaggModule.ArticlePoll, PermissionEnum.EDIT)

  useEffect(() => {
    find(Number(id))
  }, [id])

  useEffect(() => {
    if (!poll) return

    setQuestion(poll.question || {})
    setLabels(
      poll.options.reduce((acc, option) => {
        if (option.code || typeof option.label === 'string') return acc

        return { ...acc, [option.id]: { ...(option.label || {}) } }
      }, {} as Record<number, Record<string, string>>)
    )
  }, [poll?.id])

  if (!poll) {
    return (
      <Center w='full' py='20'>
        <Spinner color='white' />
      </Center>
    )
  }

  const votesFor = (option: ArticlePollOptionRow) => poll.tally?.[String(option.id)] || 0

  const saveTexts = () => {
    const options = Object.entries(labels).map(([optionId, label]) => ({ id: Number(optionId), label }))

    updateArticlePoll(poll.id, { question, options } as any)
  }

  const retire = (option: ArticlePollOptionRow) =>
    updateArticlePoll(poll.id, { options: [{ id: option.id, retired: !option.retired }] } as any)

  return (
    <Center w={'full'} pb={10}>
      <VStack color={'white'} w={'full'} maxW='860px' align='stretch'>
        <Heading my={2} textAlign='center'>Encuesta del artículo</Heading>

        <Text textAlign='center' color='gray.300'>{poll.news?.title}</Text>
        <Flex justifyContent='center' gap='2' mb='4' wrap='wrap'>
          <Badge colorScheme='purple'>{archetypeLabels[poll.archetype] || poll.archetype}</Badge>
          <Badge colorScheme={poll.source === 'manual' ? 'orange' : 'green'}>
            {sourceLabels[poll.source] || poll.source}
          </Badge>
          <Badge colorScheme={poll.active ? 'green' : 'red'}>{poll.active ? 'Activa' : 'Apagada'}</Badge>
          <Badge colorScheme='blue'>{poll.totalVotes} votos</Badge>
        </Flex>

        <Alert status={poll.source === 'manual' ? 'warning' : 'info'} borderRadius='8px' bgColor='#2b2247'>
          <AlertIcon />
          <AlertDescription fontSize='sm'>
            {poll.source === 'manual'
              ? 'Esta encuesta está congelada: fue editada a mano, así que sus opciones ya no siguen a los códigos del artículo. Si el artículo cambia, esta encuesta no cambia.'
              : 'Sigue automáticamente los códigos del cuerpo del artículo. Si guardás cualquier cambio acá, queda congelada y deja de seguirlos.'}
          </AlertDescription>
        </Alert>

        <Text fontSize='lg' fontWeight={700} mt='6'>Pregunta</Text>
        {CONTENT_LANGS.map(lang => (
          <Box key={lang} mb='3'>
            <Text fontSize='sm' color='gray.400' mb='1'>{langLabels[lang]}</Text>
            <Input
              value={question[lang] || ''}
              isDisabled={readOnly}
              onChange={e => setQuestion({ ...question, [lang]: e.target.value })}
            />
          </Box>
        ))}

        <Text fontSize='lg' fontWeight={700} mt='6'>Opciones</Text>
        <Text fontSize='sm' color='gray.400' mb='2'>
          Los códigos salen del cuerpo y no se renombran: su texto es el código. Sólo se editan las
          etiquetas de las opciones fijas. Retirar una opción no borra sus votos.
        </Text>

        <ChakraTable variant='striped' colorScheme='olaggPurple' size='sm'>
          <Thead>
            <Tr>
              <Th color='white'>Opción</Th>
              <Th color='white'>Tipo</Th>
              <Th color='white'>Votos</Th>
              <Th color='white'>Estado</Th>
              <Th color='white'>Acción</Th>
            </Tr>
          </Thead>
          <Tbody>
            {poll.options.map(option => (
              <Tr key={option.id}>
                <Td maxW='320px' whiteSpace='normal'>
                  {option.code
                    ? <Text fontFamily='mono'>{option.code}</Text>
                    : CONTENT_LANGS.map(lang => (
                      <Box key={lang} mb='1'>
                        <Input
                          size='sm'
                          placeholder={langLabels[lang]}
                          isDisabled={readOnly}
                          value={labels[option.id]?.[lang] || ''}
                          onChange={e => setLabels({
                            ...labels,
                            [option.id]: { ...(labels[option.id] || {}), [lang]: e.target.value }
                          })}
                        />
                      </Box>
                    ))}
                </Td>
                <Td>{option.code ? 'Código' : optionText(option)}</Td>
                <Td>{votesFor(option)}</Td>
                <Td>{option.retired ? 'Retirada' : 'Viva'}</Td>
                <Td>
                  <Button
                    size='xs'
                    colorScheme={option.retired ? 'green' : 'red'}
                    isDisabled={readOnly}
                    onClick={() => retire(option)}
                  >
                    {option.retired ? 'Reactivar' : 'Retirar'}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </ChakraTable>

        <Flex gap='3' mt='8'>
          <Button variant='filledGradient' flex='1' isDisabled={readOnly} onClick={saveTexts}>
            Guardar y congelar
          </Button>
          <Button variant='outlinedGradient' onClick={() => navigate(ROUTES.ARTICLE_POLLS.all)}>
            Volver
          </Button>
        </Flex>
      </VStack>
    </Center>
  )
}

export default EditArticlePoll
